import {
  Modal,
  Table,
  Tag,
  Spin,
  Empty,
  Input,
  Tooltip,
  message,
  Button,
  Form,
  InputNumber,
  Space,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { API } from "../../../config/API";
import axios from "../../../config/axiosInstance";
import { DEFAULT_CURRENCY, formatMoney } from "../../../config/countries";
import {
  DownloadOutlined,
  StopOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Search } = Input;
const { TextArea } = Input;

interface Registrar {
  id?: number;
  username: string;
  email?: string;
  code?: string | null;
  role?: { id: number; name: string } | null;
}

interface PlanInfo {
  id: number;
  plan_name: string;
  type?: string;
  price?: number | string;
  duration_type?: "days" | "months" | "years";
  duration_value?: number;
  region?: "india" | "gulf" | null;
}

interface Subscription {
  currency?: string | null;
  id: number;
  amount: string;
  payment_mode: string;
  payment_date: string;
  receipt_number: string;
  payment_reference?: string;
  subscription_start_date: string;
  subscription_end_date: string;
  status: string;
  register_mode?: string;
  zone_code?: string;
  agent_code?: string;
  notes?: string;
  created_by_admin?: Registrar | null;
  subscription_plan?: PlanInfo | null;
  createdAt: string;
}

const roleLabel = (roleId?: number): string => {
  if (roleId === 4) return "Admin";
  if (roleId === 5) return "Agent";
  if (roleId === 6) return "E-Paper Admin";
  return "";
};

interface EpaperUser {
  id: number;
  full_name: string;
  subscriber_code?: string | null;
}

interface SubscriptionHistoryProps {
  open: boolean;
  close: () => void;
  userData: EpaperUser | null;
  // Fired after a successful cancel/refund so the parent listing can
  // re-fetch and reflect the updated status without a manual reload.
  onChanged?: () => void;
}

const paymentModeLabels: Record<string, string> = {
  cash: "Cash",
  online: "Online (PayTabs)",
  bank_transfer: "Bank Transfer",
  cheque: "Cheque",
  upi: "UPI",
};

const SubscriptionHistory = (props: SubscriptionHistoryProps) => {
  const { userData } = props;
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscriberCode, setSubscriberCode] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [cancelTarget, setCancelTarget] = useState<Subscription | null>(null);
  const [refundTarget, setRefundTarget] = useState<Subscription | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [cancelForm] = Form.useForm();
  const [refundForm] = Form.useForm();
  const User = useSelector((state: any) => state?.User);
  const actorId = User?.user?.id ?? null;

  const fetchSubscriptions = async (search?: string) => {
    if (!userData) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      const queryStr = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(
        `${API.BASE_URL}epaper-users/${userData.id}/subscriptions${queryStr}`
      );
      const data = res.data;
      if (data?.data) {
        setSubscriptions(data.data);
      }
      if (data?.user?.subscriber_code) {
        setSubscriberCode(data.user.subscriber_code);
      }
    } catch (err) {
      console.error("Failed to fetch subscriptions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (props.open && userData) {
      setSearchTerm("");
      fetchSubscriptions();
    }
  }, [props.open, userData]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    fetchSubscriptions(value);
  };

  const openCancel = (sub: Subscription) => {
    cancelForm.resetFields();
    setCancelTarget(sub);
  };

  const openRefund = (sub: Subscription) => {
    refundForm.resetFields();
    refundForm.setFieldsValue({
      amount: Number(sub.amount) || 0,
    });
    setRefundTarget(sub);
  };

  const handleCancelSubmit = async (values: { reason?: string }) => {
    if (!cancelTarget || !userData) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API.BASE_URL}epaper-users/${userData.id}/subscriptions/${cancelTarget.id}/cancel`,
        { data: { reason: values.reason || null, actor_id: actorId } }
      );
      message.success("Subscription cancelled");
      setCancelTarget(null);
      cancelForm.resetFields();
      await fetchSubscriptions(searchTerm);
      props.onChanged?.();
    } catch (err) {
      console.error("Cancel error:", err);
      message.error("Failed to cancel subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefundSubmit = async (values: { amount: number; reason?: string }) => {
    if (!refundTarget || !userData) return;
    const paid = Number(refundTarget.amount) || 0;
    if (!Number.isFinite(values.amount) || values.amount <= 0) {
      message.error("Refund amount must be greater than 0");
      return;
    }
    if (values.amount > paid) {
      message.error(`Refund amount cannot exceed paid amount (${formatMoney(paid, refundTarget.currency)})`);
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API.BASE_URL}epaper-users/${userData.id}/subscriptions/${refundTarget.id}/refund`,
        { data: { amount: values.amount, reason: values.reason || null, actor_id: actorId } }
      );
      message.success(res.data?.message || "Refund processed");
      setRefundTarget(null);
      refundForm.resetFields();
      await fetchSubscriptions(searchTerm);
      props.onChanged?.();
    } catch (err) {
      console.error("Refund error:", err);
      message.error("Failed to refund subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadReceipt = async (subscriptionId: number, receiptNumber?: string) => {
    if (!userData) return;
    setDownloadingId(subscriptionId);
    try {
      const res = await axios.get(
        `${API.BASE_URL}epaper-users/${userData.id}/subscriptions/${subscriptionId}/receipt`,
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(receiptNumber || String(subscriptionId)).replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download receipt error:", err);
      message.error("Failed to download receipt");
    } finally {
      setDownloadingId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "green";
      case "expired":
        return "orange";
      case "cancelled":
        return "red";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "Receipt #",
      dataIndex: "receipt_number",
      key: "receipt_number",
      width: 260,
      render: (text: string) => (
        <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
          {text || "N/A"}
        </span>
      ),
    },
  {
      title: "Agent Info",
      dataIndex: "created_by_admin",
      key: "created_by_admin",
      width: 200,
      render: (admin: Registrar | null, record: Subscription) => {
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{admin?.username} -
              ( <span style={{ fontFamily: "monospace", color: "#0576b7" }}>
                {record.agent_code}
              </span> )</div>
          </div>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      render: (amount: string, record: Subscription) => (
        <span style={{ fontWeight: 600 }}>{formatMoney(amount, record.currency)}</span>
      ),
    },
    {
      title: "Payment Mode",
      dataIndex: "payment_mode",
      key: "payment_mode",
      width: 140,
      render: (mode: string) => (
        <Tag color="blue">{paymentModeLabels[mode] || mode}</Tag>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      key: "payment_date",
      width: 130,
      render: (date: string) =>
        date ? moment(date).format("DD-MM-YYYY") : "N/A",
    },
    {
      title: "Start Date",
      dataIndex: "subscription_start_date",
      key: "subscription_start_date",
      width: 130,
      render: (date: string) =>
        date ? moment(date).format("DD-MM-YYYY") : "N/A",
    },
    {
      title: "End Date",
      dataIndex: "subscription_end_date",
      key: "subscription_end_date",
      width: 130,
      render: (date: string) =>
        date ? moment(date).format("DD-MM-YYYY") : "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status: string) => (
        <Tag
          color={getStatusColor(status)}
          style={{ textTransform: "capitalize" }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Plan",
      dataIndex: "subscription_plan",
      key: "subscription_plan",
      width: 180,
      render: (plan: PlanInfo | null | undefined) => {
        if (!plan) return <span style={{ color: "#999" }}>—</span>;
        const dur =
          plan.duration_value && plan.duration_type
            ? `${plan.duration_value} ${
                Number(plan.duration_value) === 1
                  ? plan.duration_type.replace(/s$/, "")
                  : plan.duration_type
              }`
            : null;
        return (
            <div style={{ fontWeight: 600 }}>{plan.plan_name}</div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      fixed: "right" as const,
      render: (_: any, record: Subscription) => {
        const isActive = record.status === "active";
        return (
          <Space size="small">
            {downloadingId === record.id ? (
              <Spin size="small" />
            ) : (
              <Tooltip title="Download Receipt">
                <Button
                  type="text"
                  size="small"
                  icon={
                    <DownloadOutlined style={{ color: "#0576b7", fontSize: 16 }} />
                  }
                  onClick={() =>
                    handleDownloadReceipt(record.id, record.receipt_number)
                  }
                />
              </Tooltip>
            )}
            <Tooltip
              title={
                isActive
                  ? "Refund this subscription"
                  : "Refund only available on active subscriptions"
              }
            >
              <Button
                type="text"
                size="small"
                disabled={!isActive}
                icon={
                  <RollbackOutlined
                    style={{ color: isActive ? "#fa8c16" : undefined, fontSize: 16 }}
                  />
                }
                onClick={() => openRefund(record)}
              />
            </Tooltip>
            <Tooltip
              title={
                isActive
                  ? "Cancel this subscription"
                  : "Only active subscriptions can be cancelled"
              }
            >
              <Button
                type="text"
                size="small"
                danger
                disabled={!isActive}
                icon={<StopOutlined style={{ fontSize: 16 }} />}
                onClick={() => openCancel(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      width="95%"
      style={{ maxWidth: 1100, top: 20 }}
      title={
        <div>
          <span>Subscription History</span>
          {userData && (
            <span style={{ color: "#666", fontWeight: 400, marginLeft: 10 }}>
              - {userData.full_name}
              {subscriberCode && (
                <span
                  style={{
                    color: "#0576b7",
                    fontWeight: 600,
                    marginLeft: 8,
                  }}
                >
                  (Code: {subscriberCode})
                </span>
              )}
            </span>
          )}
        </div>
      }
      open={props?.open}
      onCancel={() => props?.close()}
      footer={null}
    >
      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Search by receipt number"
          allowClear
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={handleSearch}
          style={{ width: "100%", maxWidth: 350 }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : subscriptions.length === 0 ? (
        <Empty description="No subscriptions found" />
      ) : (
        <Table
          columns={columns}
          dataSource={subscriptions}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: "max-content" }}
        />
      )}

      {/* ── Cancel subscription confirmation ── */}
      <Modal
        width="95%"
        style={{ maxWidth: 520, top: 40 }}
        title="Cancel Subscription"
        open={!!cancelTarget}
        onCancel={() => {
          setCancelTarget(null);
          cancelForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        {cancelTarget && (
          <>
            <p>
              You are about to cancel subscription{" "}
              <strong style={{ fontFamily: "monospace" }}>
                {cancelTarget.receipt_number}
              </strong>{" "}
              ({formatMoney(cancelTarget.amount, cancelTarget.currency)}).
            </p>
            <p style={{ color: "#666", fontSize: 12 }}>
              This will mark the subscription as <Tag color="red">cancelled</Tag>{" "}
              and update the user's status if this is their latest subscription.
              The action is recorded in the subscription notes.
            </p>
            <Form
              form={cancelForm}
              layout="vertical"
              onFinish={handleCancelSubmit}
            >
              <Form.Item label="Reason (optional)" name="reason">
                <TextArea
                  rows={3}
                  maxLength={500}
                  showCount
                  placeholder="Why is this subscription being cancelled?"
                />
              </Form.Item>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                <Button
                  onClick={() => {
                    setCancelTarget(null);
                    cancelForm.resetFields();
                  }}
                >
                  Keep Subscription
                </Button>
                <Button
                  danger
                  type="primary"
                  htmlType="submit"
                  loading={actionLoading}
                >
                  Confirm Cancel
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal>

      {/* ── Refund subscription ── */}
      <Modal
        width="95%"
        style={{ maxWidth: 520, top: 40 }}
        title="Refund Subscription"
        open={!!refundTarget}
        onCancel={() => {
          setRefundTarget(null);
          refundForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        {refundTarget && (
          <>
            <p>
              Refund for subscription{" "}
              <strong style={{ fontFamily: "monospace" }}>
                {refundTarget.receipt_number}
              </strong>
              .
            </p>
            <p style={{ color: "#666", fontSize: 12 }}>
              Paid amount: <strong>{formatMoney(refundTarget.amount, refundTarget.currency)}</strong>. A full
              refund cancels the subscription; partial refunds are recorded in
              notes.
            </p>
            <Form
              form={refundForm}
              layout="vertical"
              onFinish={handleRefundSubmit}
              initialValues={{ amount: Number(refundTarget.amount) || 0 }}
            >
              <Form.Item
                label={`Refund Amount (${refundTarget.currency || DEFAULT_CURRENCY})`}
                name="amount"
                rules={[
                  { required: true, message: "Refund amount is required" },
                  {
                    validator: (_: any, v: number) => {
                      if (v == null || Number.isNaN(v)) {
                        return Promise.reject("Enter a valid amount");
                      }
                      if (v <= 0) return Promise.reject("Must be greater than 0");
                      const paid = Number(refundTarget.amount) || 0;
                      if (v > paid) {
                        return Promise.reject(
                          `Cannot exceed paid amount (${formatMoney(paid, refundTarget.currency)})`
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={Number(refundTarget.amount) || undefined}
                  step={1}
                  prefix={refundTarget.currency || DEFAULT_CURRENCY}
                />
              </Form.Item>
              <Form.Item label="Reason (optional)" name="reason">
                <TextArea
                  rows={3}
                  maxLength={500}
                  showCount
                  placeholder="Why is this refund being issued?"
                />
              </Form.Item>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                <Button
                  onClick={() => {
                    setRefundTarget(null);
                    refundForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={actionLoading}
                  style={{ background: "#fa8c16", borderColor: "#fa8c16" }}
                >
                  Process Refund
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal>
    </Modal>
  );
};

export default SubscriptionHistory;
